
// Maximum total sizes selectable per bundle. null = no limit;
// set to a number (e.g. 4) to cap selections again.
const MAX_SELECTION_COUNT = 4;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

class CinderellaService extends HTMLElement {
  constructor() {
    super();
    this.minCount = parseInt(this.dataset.minCount, 10) || 4;
    this.currentProductId = this.dataset.productId || '';
    this.errorCurrentProduct = this.dataset.errorCurrentProduct || 'Please select at least 1 size from the current product.';
    this.activeStep = 1;
    this.selections = new Map();
    this.lastFocused = null;
    this.isOpen = false;
    this.isSubmitting = false;
    // Only one Cinderella Service bundle is allowed in the cart at a time; while
    // one is present the open button locks. Seeded from the server-rendered
    // state so there is no flash before the first cart fetch resolves.
    this.isLocked = this.dataset.locked === 'true';
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onCartChange = this._onCartChange.bind(this);
  }

  connectedCallback() {
    this._cacheDom();
    this._portalModal();
    this._bind();
    this._bindCartSync();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeyDown);
    this._unbindCartSync();
    // tear down portaled nodes so they don't leak in theme editor reloads
    if (this.overlay && this.overlay.parentNode === document.body) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    if (this.modalContainer && this.modalContainer.parentNode === document.body) {
      this.modalContainer.parentNode.removeChild(this.modalContainer);
    }
  }

  /**
   * Move overlay + modal container to <body> so PDP stacking contexts
   * (sticky ATC, drawers, header) can't paint over the modal.
   */
  _portalModal() {
    if (!document.body) return;
    if (this.overlay && this.overlay.parentNode !== document.body) {
      document.body.appendChild(this.overlay);
    }
    if (this.modalContainer && this.modalContainer.parentNode !== document.body) {
      document.body.appendChild(this.modalContainer);
    }
  }

  _cacheDom() {
    this.openBtn = this.querySelector('[data-cs-open]');
    this.closeBtn = this.querySelector('[data-cs-close]');
    this.overlay = this.querySelector('[data-cs-overlay]');
    this.modalContainer = this.querySelector('[data-cs-modal-container]');
    this.modal = this.querySelector('[data-cs-modal]');
    this.header = this.querySelector('[data-cs-header]');
    this.body = this.querySelector('[data-cs-body]');
    this.footer = this.querySelector('[data-cs-footer]');
    this.steps = this.querySelectorAll('[data-cs-step]');
    this.stepButtons = {
      1: this.querySelector('[data-cs-step-btn="1"]'),
      2: this.querySelector('[data-cs-step-btn="2"]')
    };
    // Descriptions live in the header (outside the scrollable steps); show the
    // active step's copy as the step changes.
    this.stepDescriptions = {
      1: this.querySelector('[data-cs-step-desc="1"]'),
      2: this.querySelector('[data-cs-step-desc="2"]')
    };
    this.nextBtn = this.querySelector('[data-cs-next]');
    this.submitBtn = this.querySelector('[data-cs-submit]');
    this.counter = this.querySelector('[data-cs-count]');
    this.errorEl = this.querySelector('[data-cs-error]');
    this.variantButtons = this.querySelectorAll('[data-cs-variant]');
    this.infoMessage = this.querySelector('[data-cs-info-message]');
    this.limitMessage = this.querySelector('[data-cs-limit-message]');
  }

  _bind() {
    if (this.openBtn) this.openBtn.addEventListener('click', () => this.open());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    // The modal-container sits above the overlay and fills the viewport; clicks
    // landing on its empty padding (outside the inner modal) should also close.
    if (this.modalContainer) {
      this.modalContainer.addEventListener('click', (e) => {
        if (e.target === this.modalContainer) this.close();
      });
    }
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goToStep(2));
    if (this.submitBtn) this.submitBtn.addEventListener('click', () => this.submit());

    this.variantButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => this._onVariantClick(e));
    });
  }

  /* ---------------- cart sync (one-bundle lock) ---------------- */

  /**
   * Keep the open button in sync with the cart. The theme broadcasts every
   * add/remove/quantity change on the MinimogEvents bus ("cart-update"); the
   * Cinderella submit/cleanup paths only emit the DOM "cart:refresh" event, so
   * both signals are observed.
   */
  _bindCartSync() {
    const events = window.MinimogEvents;
    const evtName = window.MinimogTheme
      && window.MinimogTheme.pubSubEvents
      && window.MinimogTheme.pubSubEvents.cartUpdate;
    if (events && typeof events.subscribe === 'function' && evtName) {
      this._cartUpdateUnsub = events.subscribe(evtName, this._onCartChange);
    }
    document.addEventListener('cart:refresh', this._onCartChange);
  }

  _unbindCartSync() {
    if (typeof this._cartUpdateUnsub === 'function') {
      this._cartUpdateUnsub();
      this._cartUpdateUnsub = null;
    }
    document.removeEventListener('cart:refresh', this._onCartChange);
  }

  async _onCartChange() {
    const cart = await this._fetchCart();
    if (!cart) return;
    this._setLocked(this._cartHasCinderella(cart));
  }

  async _fetchCart() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    try {
      const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      return null;
    }
  }

  _cartHasCinderella(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some((line) => line.properties && line.properties._cinderella_service);
  }

  /**
   * Lock/unlock the open button. When locked the info message is swapped for the
   * limit message so the shopper understands why the button is disabled.
   */
  _setLocked(locked) {
    if (locked === this.isLocked) return;
    this.isLocked = locked;
    this.classList.toggle('is-locked', locked);
    this.dataset.locked = locked ? 'true' : 'false';

    if (this.openBtn) {
      this.openBtn.disabled = locked;
      if (locked) {
        this.openBtn.setAttribute('aria-disabled', 'true');
      } else {
        this.openBtn.removeAttribute('aria-disabled');
      }
    }
    if (this.infoMessage) this.infoMessage.hidden = locked;
    if (this.limitMessage) this.limitMessage.hidden = !locked;

    // A bundle already in cart while the modal sits open (e.g. added in another
    // tab) should close it — the shopper can no longer add a second one. During
    // an active submit the modal stays open so its inline error is visible.
    if (locked && this.isOpen && !this.isSubmitting) this.close();
  }

  /* ---------------- modal lifecycle ---------------- */

  open() {
    if (this.isOpen || this.isLocked) return;
    this.isOpen = true;
    this.lastFocused = document.activeElement;
    this.overlay.classList.add('is-open');
    this.modalContainer.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.modalContainer.setAttribute('aria-hidden', 'false');
    this.openBtn && this.openBtn.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKeyDown);
    this._refreshVariantData();
    // Resume the step the user left on — the element instance persists in the
    // DOM across close/open, so `activeStep` survives. A page reload builds a
    // fresh instance (activeStep = 1, empty selections), clearing everything.
    this.goToStep(this.activeStep, { skipFocus: true });
    // focus first interactive element in active step
    requestAnimationFrame(() => this._focusFirstInStep());
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.overlay.classList.remove('is-open');
    this.modalContainer.classList.remove('is-open');
    this.openBtn && this.openBtn.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', this._onKeyDown);
    this._hideError();
    // for trap focused to modal
    const focusTarget = (this.lastFocused
      && this.lastFocused !== document.body
      && typeof this.lastFocused.focus === 'function')
      ? this.lastFocused
      : this.openBtn;
    if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
    this.overlay.setAttribute('aria-hidden', 'true');
    this.modalContainer.setAttribute('aria-hidden', 'true');
  }

  _onKeyDown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key === 'Tab') {
      this._trapFocus(e);
    }
  }

  _trapFocus(e) {
    const focusables = this.modal.querySelectorAll(FOCUSABLE_SELECTOR);
    const visible = Array.from(focusables).filter((el) => {
      const step = el.closest('[data-cs-step]');
      if (step && step.getAttribute('aria-hidden') === 'true') return false;
      if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') return false;
      return true;
    });
    if (visible.length === 0) return;
    const first = visible[0];
    const last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  _focusFirstInStep() {
    const active = this.querySelector('[data-cs-step].is-active');
    if (!active) return;
    const focusable = active.querySelector(FOCUSABLE_SELECTOR);
    if (focusable) {
      focusable.focus();
    } else if (this.closeBtn) {
      this.closeBtn.focus();
    }
  }

  /* ---------------- step transitions ---------------- */

  goToStep(stepNum, opts = {}) {
    this.activeStep = stepNum;
    this.steps.forEach((step) => {
      const isActive = parseInt(step.dataset.csStep, 10) === stepNum;
      step.classList.toggle('is-active', isActive);
      this.footer.classList.toggle('is-active', isActive);
      step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
    // toggle footer buttons
    Object.keys(this.stepButtons).forEach((key) => {
      const btn = this.stepButtons[key];
      if (!btn) return;
      const show = parseInt(key, 10) === stepNum;
      btn.style.display = show ? '' : 'none';
    });
    // toggle header descriptions
    Object.keys(this.stepDescriptions).forEach((key) => {
      const el = this.stepDescriptions[key];
      if (!el) return;
      el.style.display = parseInt(key, 10) === stepNum ? '' : 'none';
    });
    // border separates the header from the scrollable list — step 2 only
    if (this.header) {
      this.header.classList.toggle('cinderella-service__header--bordered', stepNum === 2);
    }
    // scroll body to top
    if (this.body) this.body.scrollTop = 0;
    if (!opts.skipFocus) {
      requestAnimationFrame(() => this._focusFirstInStep());
    }
  }

  /* ---------------- selection logic ---------------- */

  _onVariantClick(e) {
    const btn = e.currentTarget;
    if (btn.disabled || btn.getAttribute('data-cs-variant-available') === 'false') return;
    const id = btn.dataset.csVariantId;
    if (!id) return;
    if (this.selections.has(id)) {
      this.selections.delete(id);
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
    } else {
      // Unselected buttons are greyed out (disabled) once the max is reached,
      // so reaching this branch means there is room to add another size.
      this.selections.set(id, {
        id: id,
        title: btn.dataset.csVariantTitle || '',
        price: parseInt(btn.dataset.csVariantPrice, 10) || 0,
        productId: btn.dataset.csProductId || '',
        productTitle: btn.dataset.csProductTitle || '',
        productHandle: btn.dataset.csProductHandle || '',
        inventoryQuantity: parseInt(btn.dataset.csVariantInventory, 10),
        inventoryManaged: btn.dataset.csVariantInventoryManaged === 'true',
        inventoryPolicy: btn.dataset.csVariantInventoryPolicy || 'deny'
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
    }
    this._updateCounter();
    this._refreshValidation();
  }

  _updateCounter() {
    if (this.counter) this.counter.textContent = String(this.selections.size);
  }

  _hasCurrentSelection() {
    for (const it of this.selections.values()) {
      if (String(it.productId) === String(this.currentProductId)) return true;
    }
    return false;
  }

  /**
   * Reserve the LAST remaining slot for the current product: once the shopper
   * is one pick away from the max without having chosen a current-product size,
   * the other products lock so the final size must come from the current one.
   * Tied to the max (not the minimum) so shoppers keep full freedom until the
   * very last slot.
   */
  _shouldForceCurrentOnly() {
    return MAX_SELECTION_COUNT !== null
      && this.selections.size >= MAX_SELECTION_COUNT - 1
      && !this._hasCurrentSelection();
  }

  _refreshValidation() {
    this._refreshVariantStates();
    this._updateSubmitButton();

    // Only the current-product rule shows a message — the max simply greys out
    // the remaining sizes, no explanation needed.
    if (this._shouldForceCurrentOnly()) {
      this._showError(this.errorCurrentProduct);
    } else {
      this._hideError();
    }
  }

  /**
   * Single source of truth for which variant buttons are actionable. Two rules
   * grey out (disable) unselected buttons — both replacing what used to be an
   * inline error message:
   *  - at the max selection count, every unselected size locks so the only move
   *    left is deselecting an existing pick;
   *  - once the minimum is met without a size from the current product, every
   *    OTHER product locks so the shopper can only add from the current one.
   * Selected buttons stay clickable (to deselect); sold-out buttons keep their
   * own disabled/strike-through state.
   */
  _refreshVariantStates() {
    const atMax = MAX_SELECTION_COUNT !== null && this.selections.size >= MAX_SELECTION_COUNT;
    const forceCurrentOnly = this._shouldForceCurrentOnly();

    this.variantButtons.forEach((btn) => {
      const soldOut = btn.getAttribute('data-cs-variant-available') === 'false';
      const isSelected = this.selections.has(btn.dataset.csVariantId);
      const isCurrentProduct = String(btn.dataset.csProductId) === String(this.currentProductId);

      let locked = false;
      if (!isSelected && !soldOut) {
        locked = atMax || (forceCurrentOnly && !isCurrentProduct);
      }

      btn.disabled = soldOut || locked;
      btn.classList.toggle('is-disabled', locked);
      if (soldOut || locked) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }
    });
  }

  _updateSubmitButton() {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = !(this.selections.size >= this.minCount && this._hasCurrentSelection());
  }

  _showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.classList.add('is-visible');
  }

  _hideError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.classList.remove('is-visible');
  }

  /* ---------------- cart submission ---------------- */

  async submit() {
    if (this.isSubmitting) return;
    if (this.selections.size < this.minCount) return;
    if (MAX_SELECTION_COUNT !== null && this.selections.size > MAX_SELECTION_COUNT) return;
    if (!this._hasCurrentSelection()) return;

    this.isSubmitting = true;
    this.submitBtn.classList.add('is-loading');
    this.submitBtn.disabled = true;

    try {
      // Pull live availability first — the page-load snapshot can be stale
      // (CDN cache, long-lived tab). This may drop selections that sold out.
      await this._refreshVariantData();
      if (this.selections.size < this.minCount || !this._hasCurrentSelection()) return;

      const items = Array.from(this.selections.values());

      const cart = await this._fetchCart();
      // Belt-and-braces: the open button is disabled while a bundle is in the
      // cart, but a second tab could have added one since this modal opened.
      if (this._cartHasCinderella(cart)) {
        this._setLocked(true);
        const msg = (this.limitMessage && this.limitMessage.textContent.trim())
          || 'You can add only one Cinderella Service order at a time.';
        this._showError(msg);
        return;
      }

      // Shopify only validates the primary line item on cart/add.js — the
      // other sizes live in properties and bypass inventory checks, so we
      // verify every selection against stock minus what the cart already holds.
      const unavailable = await this._findUnavailableSelection(items, cart);
      if (unavailable) {
        this._showError(
          `${unavailable.productTitle} (${unavailable.title}) is no longer available. Please pick another size.`
        );
        return;
      }

      // highest priced variant becomes the actual cart line item.
      const primary = items.reduce((acc, cur) => (cur.price > acc.price ? cur : acc), items[0]);

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      // Sort by price descending: variant_1 = primary (highest price, charged at full),
      // variant_2…N = read by Shopify Flow on order creation to add at ₹0.
      const sortedItems = [...items].sort((a, b) => b.price - a.price);

      const properties = { _cinderella_service: 'true', _cinderella_bundle_id: this._generateBundleId() };

      // Flat variant IDs for Flow (underscore-prefixed = hidden from Shopify admin UI).
      sortedItems.forEach((it, i) => { properties[`_cinderella_variant_${i + 1}_id`] = String(it.id); });

      // Display metadata for cart breakdown (underscore-prefixed = hidden from admin UI).
      const cinderellaItems = sortedItems.map((it) => {
        const [title, color = ''] = it.productTitle.split(' - ');
        return {
          title,
          color: color.replace(/^[\s-]+|[\s-]+$/g, ''),
          variant_id: parseInt(it.id, 10),
          url: it.productHandle ? `${root}products/${it.productHandle}?variant=${it.id}` : '',
          size: it.title
        };
      });
      properties._cinderella_service_items = cinderellaItems;

      const payload = {
        items: [
          {
            id: parseInt(primary.id, 10),
            quantity: 1,
            properties: properties
          }
        ],
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      };

      const res = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart.');
      }

      const addResponse = await res.json();
      this._reset();
      this.close();
      this._notifyCartUpdate(addResponse);
    } catch (err) {
      this._showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
    } finally {
      this.isSubmitting = false;
      this.submitBtn.classList.remove('is-loading');
      this._updateSubmitButton();
    }
  }

  /**
   * Refresh `available` per variant from /products/<handle>.js (cache-busted
   * with `ts` so the CDN can't serve a stale copy). The endpoint doesn't
   * expose inventory_quantity, so quantities keep their render-time values.
   */
  async _refreshVariantData() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const handles = Array.from(
      new Set(
        Array.from(this.variantButtons)
          .map((btn) => btn.dataset.csProductHandle)
          .filter(Boolean)
      )
    );
    if (handles.length === 0) return;

    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`${root}products/${handle}.js?ts=${Date.now()}`, { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      )
    );

    const availability = {};
    products.forEach((product) => {
      if (!product || !Array.isArray(product.variants)) return;
      product.variants.forEach((variant) => {
        availability[variant.id] = !!variant.available;
      });
    });
    if (Object.keys(availability).length === 0) return;
    this._applyVariantAvailability(availability);
  }

  _applyVariantAvailability(availability) {
    this.variantButtons.forEach((btn) => {
      const id = btn.dataset.csVariantId;
      if (!(id in availability)) return;
      const available = availability[id];

      btn.setAttribute('data-cs-variant-available', available ? 'true' : 'false');
      btn.classList.toggle('is-sold-out', !available);

      if (!available && this.selections.has(id)) {
        // sold out since it was picked — drop it so validation re-runs
        this.selections.delete(id);
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-pressed', 'false');
      }
    });

    this._updateCounter();
    // _refreshValidation() -> _refreshVariantStates() reconciles disabled /
    // aria-disabled for every button (availability + lock rules) in one pass.
    this._refreshValidation();
  }

  _generateBundleId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  async _findUnavailableSelection(items, cart) {
    // No cart snapshot (fetch failed) — let cart/add.js validate the primary.
    if (!cart || !Array.isArray(cart.items)) return null;

    const committed = {};
    cart.items.forEach((line) => {
      committed[line.variant_id] = (committed[line.variant_id] || 0) + line.quantity;

      if (!line.properties || !line.properties._cinderella_service) return;
      const raw = line.properties._cinderella_service_items;
      let csItems = [];
      if (Array.isArray(raw)) {
        csItems = raw;
      } else if (typeof raw === 'string') {
        try { csItems = JSON.parse(raw); } catch (err) { return; }
      }
      if (!Array.isArray(csItems)) return;
      csItems.forEach((csItem) => {
        // the primary's unit is already counted via the line quantity above
        if (!csItem || String(csItem.variant_id) === String(line.variant_id)) return;
        // each unit of the line is one full bundle, so hidden sizes scale with quantity
        committed[csItem.variant_id] = (committed[csItem.variant_id] || 0) + line.quantity;
      });
    });

    return (
      items.find((it) => {
        if (!it.inventoryManaged || it.inventoryPolicy === 'continue') return false;
        if (Number.isNaN(it.inventoryQuantity)) return false;
        const used = committed[parseInt(it.id, 10)] || 0;
        return it.inventoryQuantity - used < 1;
      }) || null
    );
  }

  _notifyCartUpdate(addResponse) {
    const drawer = document.querySelector('m-cart-drawer');

    if (drawer && addResponse && addResponse.sections && addResponse.sections['cart-drawer'] && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents(addResponse);
      } catch (e) {
        // fall through to count-only update below
      }
    } else if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
      try { drawer.onCartDrawerUpdate(); } catch (e) { /* noop */ }
    }

    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return;
        const count = cart.item_count;
        document.querySelectorAll('.m-cart-count-bubble').forEach((el) => {
          el.textContent = count;
          if (count > 0) {
            el.classList.remove('m:hidden', 'hidden');
            el.style.display = '';
          } else {
            el.classList.add('m:hidden');
          }
        });
        // Toggle empty state on the drawer host element.
        if (drawer) drawer.classList.toggle('m-cart--empty', count === 0);
      })
      .catch(() => { });

    // Emit a generic event so any other listeners (FoxKit, etc.) can react.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'cinderella-service' } }));
  }

  _reset() {
    this.activeStep = 1;
    this.selections.clear();
    this.variantButtons.forEach((btn) => {
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
    });
    this._updateCounter();
    this._refreshValidation();
  }
}

if (!customElements.get('cinderella-service')) {
  customElements.define('cinderella-service', CinderellaService);
}

/* ---------------- theme editor ---------------- */

document.addEventListener('shopify:block:select', (e) => {
  const cs = e.target && e.target.closest ? e.target.closest('cinderella-service') : null;
  if (cs && typeof cs.open === 'function') cs.open();
});
document.addEventListener('shopify:block:deselect', (e) => {
  const cs = e.target && e.target.closest ? e.target.closest('cinderella-service') : null;
  if (cs && typeof cs.close === 'function') cs.close();
});

